import axios from 'axios'

// Base URL - in development, Vite proxy handles /api prefix
const BASE_URL = import.meta.env.VITE_API_URL || '/api'

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 unauthorized - redirect to login
    if (error.response?.status === 401) {
      // Clear stored auth
      localStorage.removeItem('xafra-bi-auth')
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

// BI API functions
export const biApi = {
  // Auth
  login: (userName: string, password: string) =>
    api.post('/bi/auth/login', { user_name: userName, password }),
  
  logout: () => api.post('/bi/auth/logout'),
  
  getMe: () => api.get('/bi/auth/me'),
  
  getRateLimitStatus: (userName: string) =>
    api.get(`/bi/auth/rate-limit-status/${userName}`),
  
  // Dashboard
  getRealtimeMetrics: (params: {
    timezone?: string
    countries?: string[]
    operators?: string[]
    productIds?: number[]
    sources?: string[]
    customerIds?: number[]
    flow?: string
  }) => {
    const searchParams = new URLSearchParams()
    
    if (params.timezone) searchParams.set('timezone', params.timezone)
    if (params.countries?.length) {
      params.countries.forEach(c => searchParams.append('countries', c))
    }
    if (params.operators?.length) {
      params.operators.forEach(o => searchParams.append('operators', o))
    }
    if (params.productIds?.length) {
      params.productIds.forEach(id => searchParams.append('productIds', String(id)))
    }
    if (params.sources?.length) {
      params.sources.forEach(s => searchParams.append('sources', s))
    }
    if (params.customerIds?.length) {
      params.customerIds.forEach(id => searchParams.append('customerIds', String(id)))
    }
    if (params.flow) searchParams.set('flow', params.flow)
    
    return api.get(`/bi/dashboard/realtime?${searchParams.toString()}`)
  },
  
  getSummary: (timezone?: string) =>
    api.get('/bi/dashboard/summary', { params: { timezone } }),
  
  // Filters
  getFilterOptions: () => api.get('/bi/filters/options'),
  
  // Preferences
  getPreferences: () => api.get('/bi/preferences'),
  
  savePreferences: (preferences: {
    timezone?: string
    defaultFilters?: Record<string, any>
    dashboardLayout?: Record<string, any>
    theme?: string
    language?: string
  }) => api.put('/bi/preferences', preferences),
  
  // Reports
  getHistoricalReport: (params: {
    startDate: string
    endDate: string
    granularity?: 'hour' | 'day' | 'week'
    timezone?: string
    countries?: string[]
    operators?: string[]
    productIds?: number[]
    sources?: string[]
    customerIds?: number[]
    flow?: string
  }) => {
    const searchParams = new URLSearchParams()
    
    searchParams.set('startDate', params.startDate)
    searchParams.set('endDate', params.endDate)
    if (params.granularity) searchParams.set('granularity', params.granularity)
    if (params.timezone) searchParams.set('timezone', params.timezone)
    if (params.countries?.length) {
      params.countries.forEach(c => searchParams.append('countries', c))
    }
    if (params.operators?.length) {
      params.operators.forEach(o => searchParams.append('operators', o))
    }
    if (params.productIds?.length) {
      params.productIds.forEach(id => searchParams.append('productIds', String(id)))
    }
    if (params.sources?.length) {
      params.sources.forEach(s => searchParams.append('sources', s))
    }
    if (params.customerIds?.length) {
      params.customerIds.forEach(id => searchParams.append('customerIds', String(id)))
    }
    if (params.flow) searchParams.set('flow', params.flow)
    
    return api.get(`/bi/reports/historical?${searchParams.toString()}`)
  },
  
  exportReport: (params: {
    startDate: string
    endDate: string
    format?: 'csv' | 'json'
    timezone?: string
    countries?: string[]
    operators?: string[]
    productIds?: number[]
    sources?: string[]
  }) => {
    const searchParams = new URLSearchParams()
    
    searchParams.set('startDate', params.startDate)
    searchParams.set('endDate', params.endDate)
    if (params.format) searchParams.set('format', params.format)
    if (params.timezone) searchParams.set('timezone', params.timezone)
    if (params.countries?.length) {
      params.countries.forEach(c => searchParams.append('countries', c))
    }
    if (params.operators?.length) {
      params.operators.forEach(o => searchParams.append('operators', o))
    }
    if (params.productIds?.length) {
      params.productIds.forEach(id => searchParams.append('productIds', String(id)))
    }
    if (params.sources?.length) {
      params.sources.forEach(s => searchParams.append('sources', s))
    }
    
    return api.get(`/bi/reports/export?${searchParams.toString()}`, {
      responseType: params.format === 'csv' ? 'blob' : 'json'
    })
  },

  // Random Distribution Analysis
  getRandomAnalysis: (params: {
    startDate: string
    endDate: string
    timezone?: string
    granularity?: 'hour' | 'day' | 'week'
    countries?: string[]
    operators?: string[]
    references?: string[]
    productIds?: number[]
    customerIds?: number[]
    flow?: string
  }) => {
    const searchParams = new URLSearchParams()
    searchParams.set('startDate', params.startDate)
    searchParams.set('endDate', params.endDate)
    if (params.timezone) searchParams.set('timezone', params.timezone)
    if (params.granularity) searchParams.set('granularity', params.granularity)
    if (params.countries?.length) {
      params.countries.forEach(c => searchParams.append('countries', c))
    }
    if (params.operators?.length) {
      params.operators.forEach(o => searchParams.append('operators', o))
    }
    if (params.references?.length) {
      params.references.forEach(r => searchParams.append('references', r))
    }
    if (params.productIds?.length) {
      params.productIds.forEach(id => searchParams.append('productIds', String(id)))
    }
    if (params.customerIds?.length) {
      params.customerIds.forEach(id => searchParams.append('customerIds', String(id)))
    }
    if (params.flow) searchParams.set('flow', params.flow)
    
    return api.get(`/bi/random/analysis?${searchParams.toString()}`)
  },

  getRandomProducts: () => api.get('/bi/random/products'),
  
  getRandomReferences: () => api.get('/bi/random/references'),

  // Funnel Analysis
  getFunnelOperators: () => api.get('/bi/funnel/operators'),

  getFunnelFilters: (params?: { country?: string, operator?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.country) searchParams.set('country', params.country)
    if (params?.operator) searchParams.set('operator', params.operator)
    return api.get(`/bi/funnel/filters?${searchParams.toString()}`)
  },

  getFunnelConfig: (country: string, operator: string) =>
    api.get(`/bi/funnel/config/${country}/${operator}`),

  getFunnelAnalysis: (params: {
    country: string
    operator: string
    startDate: string
    endDate: string
    connectionType?: 'HE' | 'WIFI' | 'ALL'
    timezone?: string
    customerId?: string
    productId?: string
    reference?: string
    source?: string
  }) => {
    const searchParams = new URLSearchParams()
    searchParams.set('country', params.country)
    searchParams.set('operator', params.operator)
    searchParams.set('startDate', params.startDate)
    searchParams.set('endDate', params.endDate)
    if (params.connectionType) searchParams.set('connectionType', params.connectionType)
    if (params.timezone) searchParams.set('timezone', params.timezone)
    if (params.customerId && params.customerId !== 'ALL') searchParams.set('customerId', params.customerId)
    if (params.productId && params.productId !== 'ALL') searchParams.set('productId', params.productId)
    if (params.reference && params.reference !== 'ALL') searchParams.set('reference', params.reference)
    if (params.source && params.source !== 'ALL') searchParams.set('source', params.source)
    
    return api.get(`/bi/funnel/analysis?${searchParams.toString()}`)
  },

  // Notifications
  getNotifications: (params?: { limit?: number; offset?: number }) =>
    api.get('/bi/notifications', { params }),

  getNotificationDetail: (id: number) =>
    api.get(`/bi/notifications/${id}`),

  markNotificationRead: (id: number) =>
    api.post(`/bi/notifications/${id}/read`),

  markAllNotificationsRead: () =>
    api.post('/bi/notifications/read-all'),

  getVapidPublicKey: () =>
    api.get('/bi/notifications/vapid-public-key'),

  subscribeToPush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post('/bi/notifications/subscribe', subscription),

  unsubscribeFromPush: (endpoint: string) =>
    api.post('/bi/notifications/unsubscribe', { endpoint }),
}

export default api
